import React, { useRef, useEffect, useState } from "react";
import { leagueDashboardPath } from "../../utils/leaguePath";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import useNavRefresh from "../../utils/navRefresh";
import {
  MdKeyboardArrowRight,
  MdOutlineLogout,
  MdOutlineSpaceDashboard,
  MdOutlinePerson,
} from "react-icons/md";
import {
  mainNav,
  createPostItem,
  secondaryNav,
  accountSection,
} from "../../data/menus.js";
import LeagueMenu, { BackToQushiCard } from "./LeagueMenu";
import useLeagueContext from "../../utils/useLeagueContext";
import "./sidebar.css";
import { sidebarToggle } from "../../utils/toggler.js";
import { recordMenuNav } from "../../utils/menuNav.js";
import {
  getSidebarCollapseOverride,
  isFullScreenChartRoute,
  setSidebarCollapseOverride,
  subscribeSidebarCollapse,
} from "../../utils/sidebarCollapse.js";
import { logout } from "../../api/user/index.js";
import { toast } from "react-toastify";
import { event } from "../../utils/event";
import CookieBanner from "./CookieBanner.jsx";
import { competitionCopy } from "../../pages/Game/landing/leagueFormat";

// Shared active/inactive treatment used by every nav row: soft teal background,
// dark-teal icon + label, and a dark-teal accent on the far-right edge. The bar
// is a plain full-height sliver; the pill's own `overflow-hidden` + rounded
// corners clip its top-right/bottom-right corners so the accent follows the
// pill's rounded shape instead of sitting on top as a squared-off straight bar.
const ACCENT = (
  <span
    aria-hidden="true"
    className="absolute right-0 top-0 bottom-0 hidden w-[3px] bg-primaryColor md:block"
  />
);

function SectionLabel({ children }) {
  return (
    <p className="mb-1 mt-5 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary">
      {children}
    </p>
  );
}

// A standard sidebar nav row. Colour is driven by the row's text colour so the
// (currentColor) icon and label always stay in sync between the two states.
function NavRow({ item, onNavigate }) {
  const Icon = item.Icon;
  // Clicking the row you are already on refreshes that screen instead of doing
  // nothing. See utils/navRefresh.js — the client reported it on Trade.
  const onRowClick = useNavRefresh(onNavigate);
  return (
    <NavLink
      to={item.path}
      id={item.guideId}
      title={item.label}
      onClick={onRowClick(item.path)}
      className={({ isActive }) =>
        // Active highlight (pale-green fill + teal label) is DESKTOP ONLY. On
        // mobile the 3-dot menu shows every row neutral, with no active state.
        `relative my-0.5 flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-sm transition-colors ${
          isActive
            ? "font-medium text-secondary md:bg-veryLightGreen md:font-semibold md:text-primaryColor"
            : "font-medium text-secondary hover:bg-gray-50"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={22} className="shrink-0" />
          <span className="truncate">{item.label}</span>
          {isActive && ACCENT}
        </>
      )}
    </NavLink>
  );
}

// Standalone "Create Post" action pill. Inactive = white pill with a very soft
// near-white teal border and dark-teal label. Active = the same treatment as a
// selected nav row (soft teal fill + right-edge accent).
function CreatePostRow({ item, onNavigate }) {
  const Icon = item.Icon;
  const onRowClick = useNavRefresh(onNavigate);
  return (
    <NavLink
      to={item.path}
      title={item.label}
      onClick={onRowClick(item.path)}
      className={({ isActive }) =>
        `relative my-2 flex items-center gap-3 overflow-hidden rounded-xl px-3 py-2.5 text-sm transition-colors ${
          isActive
            ? "bg-veryLightGreen font-semibold text-primaryColor"
            : "border border-[#DCEEEB] bg-white font-medium text-primaryColor hover:bg-[#F3FAF9]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={22} className="shrink-0" />
          <span className="truncate">{item.label}</span>
          {isActive && ACCENT}
        </>
      )}
    </NavLink>
  );
}

function Sidebar({ ...props }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = JSON.parse(localStorage.getItem("accessToken"));
  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;
  const handle = userInfo?.user_name || userInfo?.full_name || "Account";

  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [avatar, setAvatar] = useState(
    () => localStorage.getItem("img") || userInfo?.image || ""
  );
  const accountRef = useRef(null);

  // Inside a JOINED league the menu's contents change — the user is in a league,
  // not the main app. Previewing a competition does not count: they aren't a
  // member yet, so they keep the normal menu (see the ticket's three states).
  const league = useLeagueContext();
  const inLeague = league.isMember;

  const hiddenSidebarRoutes = [
    "/",
    "/auth/register",
    "/auth/create-account",
    "/auth/login",
    "/auth/verification",
    "/auth/forgot-password",
  ];
  const shouldHideSidebar =
    hiddenSidebarRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/auth/register/") ||
    location.pathname.startsWith("/reset-password/");

  // Includes /<slug>/trade — see isFullScreenChartRoute.
  const isChartsRoute = isFullScreenChartRoute(location.pathname);

  // Close the mobile drawer after navigating (sidebarToggle is a no-op on
  // desktop) and dismiss the account menu.
  const closeMenus = () => {
    setAccountMenuOpen(false);
    if (props.toggle) props.toggle();
  };

  // Nav-row tap. On mobile, remember where we are (origin) and where this row
  // leads (destination) so the destination screen's back arrow can return to
  // this 3-dot menu. See utils/menuNav + DashboardHeader's back arrow. On
  // desktop this is a plain close (the sidebar is always visible).
  const handleNavClick = (item) => {
    if (window.innerWidth < 768) recordMenuNav(location.pathname, item.path);
    closeMenus();
  };

  // "View my dashboard" points at the user's own profile hub (/profile);
  // /dashboard is a stale demo template, not a real Qushi page. Inside a league it
  // resolves to that league's own dashboard instead.
  const goToDashboard = () => {
    // Client: "the League left menu column on desktop has the League Dashboard tab
    // near the bottom. That tab currently points to the league portfolio screen
    // when clicked. This button should go to the user's league profile dashboard
    // (which is this screen)" — the screen they screenshotted is the member's
    // League Record: /<slug>/profile/<their id>.
    //
    // It went to the league Portfolio because the earlier reading of "their league
    // dashboard screen" was the money screen. It is the PROFILE — the row wears
    // the member's own avatar and handle, so what it opens should be the member,
    // not their cash.
    //
    // Falls back to the league Portfolio only if the id is missing from storage,
    // since /<slug>/profile/ with no id is not a route; that keeps the row inside
    // the league either way rather than dropping it on an error page.
    const myId =
      typeof localStorage !== "undefined" ? localStorage.getItem("user_id") : null;

    // The rule moved to utils/leaguePath so the mobile header's avatar, which
    // the client has now pointed at the same screen, cannot drift from it.
    navigate(inLeague ? leagueDashboardPath(league.slug, myId) : "/profile");
    closeMenus();
  };

  const logoutUser = async () => {
    setAccountMenuOpen(false);
    setIsDisabled(true);
    try {
      setLoading(true);
      const response = await logout();
      if (response.data.success && response.data.status === 200) {
        const clearSession = () => {
          localStorage.removeItem("ticker");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("searchItem");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("userInfo");
          localStorage.removeItem("google_login");
          localStorage.removeItem("isVerified");
          localStorage.removeItem("img");
          localStorage.removeItem("user_id");
          localStorage.removeItem("chartSettings");
          localStorage.removeItem("userEmail");
          localStorage.removeItem("notificationCount");
          localStorage.removeItem("messageCount");
          localStorage.removeItem("FCM-WEB");
          localStorage.removeItem("gameurl");
          setSidebarCollapseOverride(null);
        };
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setTimeout(() => setIsDisabled(false), 2000);
        clearSession();
        setLoading(false);
        sidebarToggle();
        navigate("/");
      } else if (response.data.status === 400) {
        setIsDisabled(false);
        toast.error("Logout failed");
      }
    } catch (error) {
      setIsDisabled(false);
      toast.error("Logout failed");
      console.error("Error during logout:", error);
    }
  };

  // Preserve the charts-route collapse behaviour: when collapsed there, the
  // sidebar is hidden entirely (the toggle arrow controls the override).
  useEffect(() => {
    const computeCollapsed = () => {
      if (!isChartsRoute) return false;
      const override = getSidebarCollapseOverride();
      return override === null ? false : override;
    };
    setIsCollapsed(computeCollapsed());
    const unsubscribe = subscribeSidebarCollapse(() =>
      setIsCollapsed(computeCollapsed())
    );
    return unsubscribe;
  }, [location]);

  // Keep the account avatar in sync when the profile picture changes elsewhere.
  useEffect(() => {
    const onProfile = (e) =>
      setAvatar(e?.detail?.profileImg || localStorage.getItem("img") || "");
    window.addEventListener("profileEvent", onProfile);
    return () => window.removeEventListener("profileEvent", onProfile);
  }, []);

  // Dismiss the account menu on an outside click.
  useEffect(() => {
    if (!accountMenuOpen) return;
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [accountMenuOpen]);

  return (
    <>
      {!shouldHideSidebar && !(isCollapsed && isChartsRoute) && (
        <aside
          id="sidebar"
          className={`sidebarWrapper md:translate-x-0 -translate-x-full md:z-0 z-50 md:!w-64 ${props.className}`}
          style={{ position: "fixed" }}
        >
          <div className="flex h-full flex-col border-r-2 border-gray-100 md:w-64">
            {/* Header: centered Q badge, with a Close button on mobile only. */}
            <div className="relative flex shrink-0 items-start justify-center px-4 pt-5 pb-3">
              <img
                src="/qushi_logo.png"
                alt="Qushi"
                draggable="false"
                className="h-12 w-auto select-none"
              />
              {/* Utility link: clean dark-teal text, medium weight, no larger
                  than a nav-row label. Tap area comes from padding (p-3), and
                  right-1/top-2 offset the padding so the text still sits on the
                  content gutter with its cap-height aligned to the badge top. */}
              <button
                type="button"
                onClick={props.toggle}
                className="absolute right-1 top-2 p-3 leading-none text-sm font-medium text-[#004d4d] md:hidden"
              >
                Close
              </button>
            </div>

            {/* Inside a joined league this rail becomes the league's own — at
                both breakpoints. A league is an environment, not a screen: while
                you're in one, the left column controls it, and the app's own nav
                is reachable through "Back to QUSHi" at the bottom. */}
            {inLeague && (
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <LeagueMenu
                  game={league.game}
                  isOrganizer={league.isOrganizer}
                  onNavigate={closeMenus}
                />
              </div>
            )}

            {/* Scrollable nav column, with soft white fade masks at both ends
                so items don't clip harshly against the pinned logo / account. */}
            <div
              className={`relative min-h-0 flex-1 flex-col ${
                inLeague ? "hidden" : "flex"
              }`}
            >
            <nav className="sidebarNav px-4 pb-2">
              {/* Primary nav + Create Post — desktop only (mobile uses the
                  bottom navigation bar for these destinations). */}
              <div className="hidden md:block">
                <SectionLabel>Main</SectionLabel>
                {mainNav.map((item) => (
                  <NavRow key={item.label} item={item} onNavigate={() => handleNavClick(item)} />
                ))}
                <CreatePostRow item={createPostItem} onNavigate={closeMenus} />
              </div>

              {secondaryNav.map((section) => (
                <div key={section.title}>
                  <SectionLabel>{section.title}</SectionLabel>
                  {section.items.map((item) => (
                    <NavRow
                      key={item.label}
                      item={item}
                      onNavigate={() => handleNavClick(item)}
                    />
                  ))}
                </div>
              ))}

              {/* Settings sits near the bottom, separated from the product nav. */}
              <SectionLabel>{accountSection.title}</SectionLabel>
              {accountSection.items.map((item) => (
                <NavRow key={item.label} item={item} onNavigate={() => handleNavClick(item)} />
              ))}
            </nav>
              {/* Top fade — just below the pinned logo */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-5 bg-gradient-to-b from-white to-transparent"
              />
              {/* Bottom fade — just above the pinned account block */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-5 bg-gradient-to-t from-white to-transparent"
              />
            </div>

            {/* Account row pinned at the bottom, with an upward menu. Hidden on
                mobile inside a league — there the 3-dot menu is a short list and
                "Back to QUSHi" is the only thing that should be pinned under it. */}
            {token && (
              <div
                ref={accountRef}
                className={`relative shrink-0 px-4 pt-2 ${
                  inLeague ? "hidden pb-2 md:block" : "pb-4"
                }`}
              >
                {/* Client Rev 3, desktop: "Inside the Leagues left menu bar, we
                    have two tabs at the bottom. Remove the 'logout' feature from
                    the avatar username button. This button will now only have one
                    option and will no longer open a drop-down modal. When the
                    button is clicked, it takes the user to their league dashboard
                    screen… No modal menus. Buttons go directly to the screen. No
                    change to the main site's left-menu column."
                    So the upward menu is suppressed while `inLeague` — the row
                    below navigates instead of toggling. Outside a league this is
                    the same account menu it has always been, untouched. */}
                {accountMenuOpen && !inLeague && (
                  <div className="absolute bottom-full left-4 right-4 mb-2 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={goToDashboard}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-secondary hover:bg-gray-50"
                    >
                      <MdOutlineSpaceDashboard size={20} className="shrink-0" />
                      View my dashboard
                    </button>
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        event("LogOutUser");
                        logoutUser();
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-60"
                    >
                      {/* Icon stays put; the label swap is the whole signal.
                          `isDisabled` already blocks a second tap. */}
                      <MdOutlineLogout size={20} className="shrink-0" />
                      {loading ? "Signing out" : "Logout"}
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={
                    inLeague
                      ? () => {
                          // goToDashboard resolves to the member's own league
                          // profile — their League Record inside this league. See
                          // the client quote on goToDashboard.
                          goToDashboard();
                          closeMenus();
                        }
                      : () => setAccountMenuOpen((open) => !open)
                  }
                  className="flex w-full items-center gap-3 rounded-2xl bg-[#F6F8F9] px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
                >
                  {avatar ? (
                    <img
                      src={avatar}
                      alt=""
                      onError={() => setAvatar("")}
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-500">
                      <MdOutlinePerson size={22} />
                    </span>
                  )}
                  {/* Two lines inside a league, matching "Back to QUSHi" below it,
                      because the client's spec draws them as a pair:
                          avatar tapeandtime / League Dashboard
                          avatar Back to QUSHi / Leave League Play
                      Outside a league it stays the single handle line it was — the
                      main menu's account row is explicitly out of scope. */}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-gray-800">
                      {handle}
                    </span>
                    {inLeague && (
                      <span className="block truncate text-xs text-secondary">
                        {competitionCopy(league.game).dashboard}
                      </span>
                    )}
                  </span>
                  <MdKeyboardArrowRight
                    size={22}
                    className={`shrink-0 text-gray-400 transition-transform ${
                      accountMenuOpen && !inLeague ? "-rotate-90" : ""
                    }`}
                  />
                </button>
              </div>
            )}

            {/* The way out of the league, pinned below the account row — the last
                thing in the column, because it's the one row that isn't part of
                this league. */}
            {inLeague && (
              <div className="shrink-0 px-4 pb-4 pt-0 md:pt-0">
                <BackToQushiCard onNavigate={closeMenus} />
              </div>
            )}
          </div>
        </aside>
      )}

      {props.className === "mobile" && (
        <div
          id="overlaySidebar"
          onClick={props.toggle}
          className="hidden absolute w-full h-screen bg-black z-10 inset-0 opacity-60"
        >
          <div></div>
        </div>
      )}

      {/* Cookie Banner */}
      <CookieBanner />
    </>
  );
}

export default Sidebar;
